import { GetServerSideProps, NextPage } from 'next';
import { useRouter } from 'next/router';

import Search from '@screens/Search';
import getConfig from 'next/config';
import { searchClient } from '@services/businessContainer';
import { injectLayoutData } from '@utils/layout';
import { useTranslations } from 'next-intl';
import { InstantSearch, useInstantSearch } from 'react-instantsearch-core';
import { NextSeo } from 'next-seo';
import { useInstantSearchNextRouting } from '@hooks/useInstantSearchNextRouting';
import { VirtualTrackSearchFilters } from '@components/VirtualTrackSearchFilters';
import {
  generateProductIndexName,
  getChannelFromLocale,
} from '@monofront/business/utils';
import { DeliveryBarCTA } from '@features/DeliveryBar';

const {
  publicRuntimeConfig: { appBaseUrl, algoliaConfig },
} = getConfig();

type SearchPageProps = {
  locale: string;
};

const NextSeoSearch = () => {
  const router = useRouter();
  const { indexUiState } = useInstantSearch();

  const tCatalog = useTranslations('Catalog');

  const urlPath = router.asPath.split('?')[0];

  return (
    <NextSeo
      title={indexUiState.query || tCatalog('search')}
      canonical={`${appBaseUrl}${urlPath}`}
      openGraph={{
        url: `${appBaseUrl}${urlPath}`,
        title: indexUiState.query || tCatalog('search'),
      }}
    />
  );
};

const SearchPage: NextPage<SearchPageProps> = ({ locale }) => {
  const indexName = generateProductIndexName(
    algoliaConfig.environment,
    false,
    getChannelFromLocale(locale),
  );

  const instantSearchRouting = useInstantSearchNextRouting(indexName, '');

  return (
    <InstantSearch
      indexName={indexName}
      searchClient={searchClient}
      routing={instantSearchRouting}
      insights
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <NextSeoSearch />
      <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-px-3 desktop:tw-mx-auto desktop:tw-min-w-[500px] desktop:tw-max-w-[500px] desktop:tw-p-0">
        <DeliveryBarCTA />
      </div>
      <VirtualTrackSearchFilters />
      <Search />
    </InstantSearch>
  );
};

export const getStaticProps = injectLayoutData<
  SearchPageProps,
  GetServerSideProps<SearchPageProps>
>(async ({ params, locale }) => {
  if (!locale) {
    return {
      notFound: true,
    };
  }

  try {
    return {
      props: {
        key: `${params?.query}-${params?.page ?? 1}`,
        locale: `${locale}`,
      },
      revalidate: 1800, // 30min
    };
  } catch (error: any) {
    return {
      locale: `${locale}`,
      notFound: true,
    };
  }
});

export default SearchPage;
